import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { shadows } from '@galapagos/design-system/shadows';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';
import { ensureFocusVisibleStyle } from '@galapagos/design-system/web';

type TypographyToken = keyof typeof typography;

export const INCENTIVE_REMINDER_STYLE_ID = 'signup-incentive-reminder-style';
const INCENTIVE_REMINDER_FOCUS_STYLE_ID = 'signup-incentive-reminder-focus-style';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

const CARD_SHADOW = shadows.shadow400;

/**
 * The narrowest viewport where the card can float in the bottom-right corner
 * without covering the page: a card no wider than the page column, inset from
 * the right edge, sits wholly right of a centred column of that same width.
 * Narrower viewports keep the card in the page flow above the step's actions.
 */
export const INCENTIVE_REMINDER_FLOAT_MIN_WIDTH =
  sizing.column.max + 2 * (sizing.column.max + spacing.lg);

/**
 * Production's gift-card reminder card, drawn with tokens: a bordered, raised
 * container card with the title and description beside the illustration and a
 * close button in the top corner. The text wraps and the card grows with it;
 * when it floats it scrolls inside itself rather than running off screen.
 */
export function incentiveReminderCss(): string {
  return `
.signup-incentive { display: flex; align-items: flex-start; gap: ${spacing.s}px; padding: ${spacing.lg}px ${spacing.s}px ${spacing.lg}px ${spacing.m}px; background: ${colors.background.container}; border: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; border-radius: ${radius.lg}px; box-shadow: ${CARD_SHADOW.offset.width}px ${CARD_SHADOW.offset.height}px ${CARD_SHADOW.radius}px ${CARD_SHADOW.spread}px ${CARD_SHADOW.color}; }
.signup-incentive__text { display: flex; flex-direction: column; gap: ${spacing.s}px; flex: 1 1 0; min-width: 0; align-self: center; }
.signup-incentive__title { margin: 0; color: ${colors.text.primary}; ${font('b2Bold')} }
.signup-incentive__description { margin: 0; color: ${colors.text.primary}; ${font('b3Regular')} }
.signup-incentive__description strong { font-weight: ${typography.b3Bold.fontWeight}; }
.signup-incentive__image { flex-shrink: 0; width: ${sizing.illustration.sm}px; max-width: 33%; height: auto; align-self: center; }
.signup-incentive__close { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: ${sizing.touchTarget.minimum}px; height: ${sizing.touchTarget.minimum}px; margin: -${spacing.m}px 0 0; padding: 0; border: none; border-radius: ${radius.rounded}px; background: transparent; color: ${colors.text.primary}; cursor: pointer; }
@media (hover: hover) {
  .signup-incentive__close:hover { background: ${colors.background.primary}; }
}
@media (min-width: ${INCENTIVE_REMINDER_FLOAT_MIN_WIDTH}px) {
  .signup-incentive { position: fixed; right: ${spacing.lg}px; bottom: ${spacing.lg}px; width: ${sizing.column.max}px; max-width: ${sizing.column.max}px; max-height: calc(100vh - ${2 * spacing.lg}px); overflow-y: auto; box-sizing: border-box; }
}
`;
}

/** Injects the card stylesheet and its focus ring once per document. No-op during prerender. */
export function ensureIncentiveReminderStyle(): void {
  if (typeof document === 'undefined') return;
  ensureFocusVisibleStyle(INCENTIVE_REMINDER_FOCUS_STYLE_ID, ['.signup-incentive__close']);
  if (document.getElementById(INCENTIVE_REMINDER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = INCENTIVE_REMINDER_STYLE_ID;
  style.textContent = incentiveReminderCss();
  document.head.appendChild(style);
}
